import "./compare-styles.scss";

import { Chip, ChipBackgroundColor, ChipSize } from "@amzn/stencil-react-components/chip";
import { Col, Flex, Row, View } from "@amzn/stencil-react-components/layout";
import { StarRatingDisplay, StarRatingDisplaySize } from "@amzn/stencil-react-components/star-rating";
import { Text } from "@amzn/stencil-react-components/text";
import { IconBin, IconCheckCircleFill, IconShow, IconSize } from "@amzn/stencil-react-icons";
import { Link } from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import { getString } from "common/uistringlabels/uiStringUtils";
import { AIForMeTool, COMPARE_TABLE_RECOMMENDATION_TYPE_LABELS } from "lib/models/aiforme-models";
import React, { FC } from "react";

import { DataClassificationChip } from "../AiForMeDataClassificationChip";
import { formatViewCount } from "../helpers";

export interface CompareTableProps {
  /** Array of tools to compare */
  tools: AIForMeTool[];
  /** Callback to remove a tool from comparison */
  onRemoveTool: (taxonomyId: string) => void;
}

/**
 * CompareTable component - Displays comparison data using Stencil layout components
 */
export const CompareTable: FC<CompareTableProps> = ({ tools, onRemoveTool }) => {
  /**
   * Render the delete button cell
   */
  const renderDeleteCell = (tool: AIForMeTool) => (
    <View className="compare-table__column compare-table__column--delete">
      <button
        type="button"
        className="compare-table__delete-btn"
        onClick={() => onRemoveTool(tool.taxonomyId)}
        aria-label={getString("aiForMe.compare.removeToolAriaLabel", { title: tool.title })}
        data-testid={`compare-table-delete-${tool.taxonomyId}`}
      >
        <IconBin size={IconSize.Small} color="red70" />
      </button>
    </View>
  );

  /**
   * Render the tool name cell with link
   */
  const renderToolNameCell = (tool: AIForMeTool) => (
    <View
      className="compare-table__column compare-table__column--name"
      style={{ display: "flex", justifyContent: "center", alignContent: "center" }}
    >
      <div style={{ minWidth: "140px", maxWidth: "140px", textAlign: "left" }}>
        <Link
          href={tool.learnMoreLink}
          target="_blank"
          rel="noopener noreferrer"
          touchpoint="compare-tool-link"
          data-testid={`compare-table-link-${tool.taxonomyId}`}
          fontSize={"T200"}
          fontStyle="normal"
          fontWeight={"bold"}
          lineHeight={"text.lineHeight.150"}
        >
          {tool.title}
        </Link>
      </div>
    </View>
  );

  /**
   * Render the views & ratings cell
   */
  const renderViewsRatingsCell = (tool: AIForMeTool) => {
    const views = tool.catalogicalGenerated?.interactions?.clicks ?? 0;
    const averageRating = tool.feedback?.averageRating ?? 0;

    return (
      <View className="compare-table__column compare-table__column--ratings" color="neutral50">
        <Col gridGap="S100" alignItems="center">
          <Row alignItems="center" gridGap="S100">
            <IconShow size={IconSize.Small} />
            <Text fontSize="T100">{getString("aiForMe.compare.table.views", { count: formatViewCount(views) })}</Text>
          </Row>
          <Row alignItems="center" gridGap="S100">
            <Text fontSize="T100">{averageRating.toFixed(2)}</Text>
            <StarRatingDisplay value={averageRating} size={StarRatingDisplaySize.ExtraSmall} />
          </Row>
        </Col>
      </View>
    );
  };

  /**
   * Render the use case cell (categories)
   */
  const renderUseCaseCell = (tool: AIForMeTool) => (
    <View className="compare-table__column compare-table__column--usecase">
      <Flex gridGap="S100" flexWrap="wrap" alignItems="center" flexDirection="column">
        {tool.categories.map((category) => (
          <Chip key={category} size={ChipSize.ExtraSmall}>
            {category}
          </Chip>
        ))}
      </Flex>
    </View>
  );

  /**
   * Render the data classification cell
   */
  const renderDataClassificationCell = (tool: AIForMeTool) => {
    const classification = tool.dataClassification ?? "Public";

    return (
      <View className="compare-table__column compare-table__column--classification">
        <DataClassificationChip label={classification} />
      </View>
    );
  };

  /**
   * Render the recommendation type cell
   */
  const renderRecommendationCell = (tool: AIForMeTool) => {
    const NON_EXISTING_SORTING_PRIORITY = -1;
    const toolSortingPriority = tool.sortingPriority ?? NON_EXISTING_SORTING_PRIORITY;

    // check if the sortingPriority has a label assigned

    const recommendationTypeStringPath: string =
      COMPARE_TABLE_RECOMMENDATION_TYPE_LABELS.find((priority) => priority.sortingPriorityValue === toolSortingPriority)
        ?.recommendationTypeUiStringPath ?? "aiForMe.compare.table.recommendation.types.notApplicable";

    const rowContent =
      recommendationTypeStringPath !== "aiForMe.compare.table.recommendation.types.notApplicable" ? (
        <Chip
          key="recommendedToolCompareTable"
          data-testid="recommendedToolCompareTable"
          size={ChipSize.ExtraSmall}
          backgroundColor={ChipBackgroundColor.Green}
          icon={<IconCheckCircleFill size={IconSize.ExtraSmall} />}
        >
          {getString(recommendationTypeStringPath)}
        </Chip>
      ) : (
        <Text key={"toolWithouthRecommendedType"} fontSize={"T100"}>
          {getString(recommendationTypeStringPath)}
        </Text>
      );

    return (
      <View className="compare-table__column compare-table__column--recommendation">
        {recommendationTypeStringPath && (
          <Row
            className="compare-table__recommendation-badge compare-table__recommendation-badge--recommend"
            alignItems="center"
            gridGap="S100"
          >
            {rowContent}
          </Row>
        )}
      </View>
    );
  };

  /**
   * Render a single tool row
   */
  const renderToolRow = (tool: AIForMeTool) => (
    <React.Fragment key={tool.taxonomyId}>
      <Row
        className="compare-table__row"
        alignItems="center"
        data-testid={`compare-row-${tool.taxonomyId}`}
        minHeight={"12rem"}
      >
        {renderDeleteCell(tool)}
        {renderToolNameCell(tool)}
        {renderViewsRatingsCell(tool)}
        {renderUseCaseCell(tool)}
        {renderDataClassificationCell(tool)}
        {renderRecommendationCell(tool)}
      </Row>
    </React.Fragment>
  );

  return (
    <View className="compare-table-container" data-testid="compare-table" width={"100%"}>
      <Col className="compare-table" width={"100%"} minWidth={"900px"}>
        {/* Header Row */}
        <Row className="compare-table__header-row" alignItems="center">
          <View className="compare-table__column compare-table__column--delete compare-table__header" />
          <View className="compare-table__column compare-table__column--name compare-table__header">
            <Text fontWeight="bold" fontSize="T300" fontStyle="normal">
              {getString("aiForMe.compare.table.toolName")}
            </Text>
          </View>
          <View className="compare-table__column compare-table__column--ratings compare-table__header">
            <Text fontWeight="bold" fontSize="T300">
              {getString("aiForMe.compare.table.viewsRatings")}
            </Text>
          </View>
          <View className="compare-table__column compare-table__column--usecase compare-table__header">
            <Text fontWeight="bold" fontSize="T300">
              {getString("aiForMe.compare.table.useCase")}
            </Text>
          </View>
          <View className="compare-table__column compare-table__column--classification compare-table__header">
            <Text fontWeight="bold" fontSize="T300">
              {getString("aiForMe.compare.table.dataClassification")}
            </Text>
          </View>
          <View
            className="compare-table__column compare-table__column--recommendation compare-table__header"
            flexDirection="column"
          >
            <Text fontWeight="bold" fontSize="T300">
              {getString("aiForMe.compare.table.recommendation.recommendation")}
            </Text>
            <Text fontWeight="bold" fontSize="T300">
              {getString("aiForMe.compare.table.recommendation.type")}
            </Text>
          </View>
        </Row>

        {/* Tool Rows */}
        {tools.map(renderToolRow)}
      </Col>
    </View>
  );
};

export default CompareTable;
